import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNotifications } from '../../contexts/NotificationContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TYPE_ICONS = {
  task_status_changed:  '🔄',
  agent_task_created:   '🤖',
  agent_response_added: '💬',
  credit_allocated:     '💳',
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60)           return 'hace un momento';
  if (diff < 3600)         return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400)        return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} d`;
}

// ─── Dropdown panel ───────────────────────────────────────────────────────────

function NotificationPanel({ anchorRef, onClose }) {
  const {
    notifications,
    loading,
    refresh,
    handleMarkRead,
    handleMarkAllRead,
    handleDelete,
    handleDeleteRead,
  } = useNotifications();

  const panelRef = useRef(null);

  // Position the panel near the bell icon, opening up or down based on available space
  const getStyle = () => {
    if (!anchorRef.current) return {};
    const rect = anchorRef.current.getBoundingClientRect();
    const panelWidth = 360;
    const margin = 8;

    const left = Math.max(margin, Math.min(rect.left, window.innerWidth - panelWidth - margin));
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const maxPanelHeight = 480;

    if (spaceBelow >= 200 || spaceBelow >= spaceAbove) {
      // Open downward
      return {
        position: 'fixed',
        top: rect.bottom + margin,
        left,
        zIndex: 99999,
        width: panelWidth,
        maxHeight: Math.max(200, spaceBelow),
      };
    } else {
      // Open upward
      return {
        position: 'fixed',
        bottom: window.innerHeight - rect.top + margin,
        left,
        zIndex: 99999,
        width: panelWidth,
        maxHeight: Math.min(maxPanelHeight, spaceAbove),
      };
    }
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        anchorRef.current && !anchorRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [anchorRef, onClose]);

  const hasUnread = notifications.some((n) => !n.read_at);

  return createPortal(
    <div
      ref={panelRef}
      style={getStyle()}
      className="bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
        <span className="font-semibold text-gray-800 text-sm">Notificaciones</span>
        <div className="flex items-center gap-2">
          {hasUnread && (
            <button
              onClick={() => handleMarkAllRead()}
              className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
              title="Marcar todas como leídas"
            >
              Marcar leídas
            </button>
          )}
          <button
            onClick={() => { refresh(); }}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Actualizar"
          >
            <i className="fas fa-sync-alt text-xs" />
          </button>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Cerrar"
          >
            <i className="fas fa-times text-xs" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="overflow-y-auto" style={{ maxHeight: 420 }}>
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <i className="fas fa-spinner fa-spin text-gray-400 text-xl" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <i className="fas fa-bell-slash text-3xl mb-2 block" />
            <span className="text-sm">Sin notificaciones</span>
          </div>
        ) : (
          <ul>
            {notifications.map((n) => (
              <li
                key={n.id}
                className={`flex gap-3 px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-default ${
                  !n.read_at ? 'bg-blue-50' : ''
                }`}
              >
                {/* Icon */}
                <span className="text-xl flex-shrink-0 mt-0.5">
                  {TYPE_ICONS[n.type] || '🔔'}
                </span>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm leading-tight ${!n.read_at ? 'font-semibold text-gray-800' : 'text-gray-700'}`}>
                    {n.title}
                  </p>
                  {n.body && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-1 flex-shrink-0">
                  {!n.read_at && (
                    <button
                      onClick={() => handleMarkRead(n.id)}
                      className="text-blue-400 hover:text-blue-600 transition-colors"
                      title="Marcar como leída"
                    >
                      <i className="fas fa-check text-xs" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(n.id)}
                    className="text-gray-300 hover:text-red-500 transition-colors"
                    title="Eliminar"
                  >
                    <i className="fas fa-trash text-xs" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Footer */}
      {notifications.some((n) => n.read_at) && (
        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-right">
          <button
            onClick={() => handleDeleteRead()}
            className="text-xs text-gray-400 hover:text-red-500 transition-colors"
          >
            Eliminar leídas
          </button>
        </div>
      )}
    </div>,
    document.body
  );
}

// ─── Bell icon button ─────────────────────────────────────────────────────────

export default function NotificationBell() {
  const { unreadCount } = useNotifications();
  const [open, setOpen] = useState(false);
  const bellRef = useRef(null);

  return (
    <>
      <button
        ref={bellRef}
        onClick={() => setOpen((o) => !o)}
        className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none transition-colors"
        aria-label="Notificaciones"
        title="Notificaciones"
      >
        <span style={{ position: 'relative', display: 'inline-block' }}>
          <i className="fas fa-bell text-lg" />
          {unreadCount > 0 && (
            <span
              className="flex items-center justify-center bg-red-500 text-white rounded-full font-bold"
              style={{
                position: 'absolute',
                top: -6,
                right: -8,
                fontSize: 10,
                minWidth: 16,
                height: 16,
                padding: '0 4px',
                lineHeight: 1,
              }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </span>
      </button>

      {open && (
        <NotificationPanel
          anchorRef={bellRef}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
